import { computed, effect, inject, untracked } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LocalStorageService } from '@df/browser/storage/local-storage.service';
import { NCatalogUi } from '@df/catalog-ui/df-catalog-ui.config';
import { ListingSize } from '@df/catalog-ui/listing/size/listing-size';
import { Client } from '@df/core/service/client';
import { patchState, signalStoreFeature, type, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { setAllEntities, withEntities } from '@ngrx/signals/entities';
import compact from 'lodash-es/compact';
import isEqual from 'lodash-es/isEqual';
import { AdvancedCommerceStore } from '../advanced-commerce-store.namespace';
import { AdvancedCommerceStoreService, injectRouteParams } from '../advanced-commerce-store.service';

export function withListing() {
    return signalStoreFeature(
        {
            state: type<AdvancedCommerceStore.State & AdvancedCommerceStore.Meta.State & AdvancedCommerceStore.Response.State>(),
            methods: type<{
                dataSet(data: AdvancedCommerceStore.Data): void;
            }>()
        },
        withState<AdvancedCommerceStore.Listing.State>({
            listingSize: undefined,
            listingCurrentSize: 4,
            sorting: undefined,
            filtersPriceFrom: 0,
            filtersPriceTo: 0,
            filtersSelection: []
        }),
        withEntities({ entity: type<AdvancedCommerceStore.Sorting.Item>(), collection: 'sorting' }),
        withEntities({ entity: type<AdvancedCommerceStore.Filters.Filter>(), collection: 'filters' }),
        withComputed(store => {
            const routeParams = injectRouteParams();

            return {
                queryParamSort: computed<string>(() => routeParams()['sort']),
                /**
                 * Whether the store has filters with selection
                 */
                isFiltered: computed(() => store.filtersEntities().some(filter => filter.selected)),
                /**
                 * Filters with options
                 */
                filtersWithOptions: computed(() =>
                    store.filtersEntities().filter(filter => {
                        if (filter.type === 'range') {
                            return true;
                        }

                        return !!filter.options?.find(option => !!option?.count);
                    })
                ),
                /**
                 * Filters with matches (`selectedCount > 0`)
                 */
                filtersWithMatches: computed(() => store.filtersEntities().filter(filter => filter.selectedCount > 0)),
                /**
                 * Filters with selection (`selected`)
                 */
                filtersWithSelection: computed(() => store.filtersEntities().filter(filter => filter.selected))
            };
        }),
        withMethods(store => {
            return {
                setListingSize(listingCurrentSize: number) {
                    patchState(store, { listingCurrentSize });
                },
                sortingAllSet(sorting: AdvancedCommerceStore.Sorting.Item[]) {
                    patchState(store, setAllEntities(sorting, { collection: 'sorting' }));
                },
                sortingBy: (value: AdvancedCommerceStore.Sorting.Item | string) => {
                    // Reset paging request
                    patchState(store, state => ({ ...state, metaIsPagingRequest: false }));

                    if (typeof value === 'string' || typeof value === 'undefined') {
                        patchState(store, { sorting: value });
                    } else {
                        patchState(store, { sorting: value.code });
                    }
                },
                filtersAllSet(filters: AdvancedCommerceStore.Filters.Filter[]) {
                    patchState(store, setAllEntities(filters, { collection: 'filters' }));
                },
                filtersSetSelection(filtersSelection: AdvancedCommerceStore.Filters.Selection[]) {
                    patchState(store, { filtersSelection });
                },
                filtersOptionSelect(filter: AdvancedCommerceStore.Filters.Filter, option: AdvancedCommerceStore.Filters.Option) {
                    const filtersSelection = store.filtersSelection();

                    // Reset paging request
                    patchState(store, state => ({ ...state, metaIsPagingRequest: false }));

                    if (filtersSelection.some(selection => selection.id === filter.id)) {
                        const selection = filtersSelection.find(selection => selection.id === filter.id);
                        const item = selection?.items?.find(item => item.id === option.id);

                        if (selection && selection.type === 'value' && !item) {
                            patchState(store, {
                                filtersSelection: filtersSelection.map(selection => {
                                    if (selection.id === filter.id) {
                                        return {
                                            ...selection,
                                            items: [
                                                ...selection.items,
                                                {
                                                    id: option.id
                                                }
                                            ]
                                        };
                                    }

                                    return selection;
                                })
                            });
                        }
                    } else {
                        patchState(store, {
                            filtersSelection: [
                                ...filtersSelection,
                                {
                                    id: filter.id,
                                    type: 'value',
                                    items: [
                                        {
                                            id: option.id
                                        }
                                    ]
                                }
                            ]
                        });
                    }
                },
                filtersOptionUnselect(filter: AdvancedCommerceStore.Filters.Filter, option: AdvancedCommerceStore.Filters.Option) {
                    const filtersSelection = store.filtersSelection();

                    // Reset paging request
                    patchState(store, state => ({ ...state, metaIsPagingRequest: false }));

                    if (filtersSelection.some(selection => selection.id === filter.id)) {
                        const selection = filtersSelection.find(selection => selection.id === filter.id);

                        if (selection && selection.type === 'value') {
                            patchState(store, {
                                filtersSelection:
                                    compact(
                                        filtersSelection.map(selection => {
                                            if (selection.id === filter.id) {
                                                const newItems = selection.items.filter(item => item.id !== option.id);

                                                if (newItems.length) {
                                                    return {
                                                        ...selection,
                                                        items: newItems
                                                    };
                                                } else {
                                                    return undefined;
                                                }
                                            }

                                            return selection;
                                        })
                                    ) || []
                            });
                        }
                    }
                },
                filtersPriceFromSet(filter: AdvancedCommerceStore.Filters.Filter, value: number) {
                    const filtersSelection = store.filtersSelection();

                    patchState(store, { filtersPriceFrom: value });

                    if (filtersSelection.some(selection => selection.id === filter.id)) {
                        const selection = filtersSelection.find(selection => selection.id === filter.id);

                        if (selection && selection.type === 'range') {
                            const item = selection?.items?.find(item => item.id === filter.id);

                            if (!value && !item?.to) {
                                // remove price selection
                                patchState(store, {
                                    filtersSelection: filtersSelection.filter(selection => selection.id !== filter.id)
                                });
                            } else {
                                patchState(store, {
                                    filtersSelection: filtersSelection.map(selection => {
                                        if (selection.id === filter.id) {
                                            return {
                                                ...selection,
                                                items: [
                                                    ...selection.items.filter(item => item.id !== filter.id),
                                                    {
                                                        id: filter.id,
                                                        from: value,
                                                        to: item?.to
                                                    }
                                                ]
                                            };
                                        }

                                        return selection;
                                    })
                                });
                            }
                        }
                    } else {
                        patchState(store, {
                            filtersSelection: [
                                ...filtersSelection,
                                {
                                    id: filter.id,
                                    type: 'range',
                                    items: [
                                        {
                                            id: 'price',
                                            from: value,
                                            to: undefined
                                        }
                                    ]
                                }
                            ]
                        });
                    }
                },
                filtersPriceToSet(filter: AdvancedCommerceStore.Filters.Filter, value: number) {
                    const filtersSelection = store.filtersSelection();

                    patchState(store, { filtersPriceTo: value });

                    if (filtersSelection.some(selection => selection.id === filter.id)) {
                        const selection = filtersSelection.find(selection => selection.id === filter.id);

                        if (selection && selection.type === 'range') {
                            const item = selection?.items?.find(item => item.id === filter.id);

                            if (!value && !item?.from) {
                                // remove price selection
                                patchState(store, {
                                    filtersSelection: filtersSelection.filter(selection => selection.id !== filter.id)
                                });
                            } else {
                                patchState(store, {
                                    filtersSelection: filtersSelection.map(selection => {
                                        if (selection.id === filter.id) {
                                            return {
                                                ...selection,
                                                items: [
                                                    ...selection.items.filter(item => item.id !== filter.id),
                                                    {
                                                        id: filter.id,
                                                        from: item?.from,
                                                        to: value
                                                    }
                                                ]
                                            };
                                        }

                                        return selection;
                                    })
                                });
                            }
                        }
                    } else {
                        patchState(store, {
                            filtersSelection: [
                                ...filtersSelection,
                                {
                                    id: filter.id,
                                    type: 'range',
                                    items: [
                                        {
                                            id: 'price',
                                            from: undefined,
                                            to: value
                                        }
                                    ]
                                }
                            ]
                        });
                    }
                },
                filtersResetSelection() {
                    patchState(store, { filtersSelection: [], filtersPriceFrom: 0, filtersPriceTo: 0 });
                }
            };
        }),
        withHooks(store => {
            const client = inject(Client);
            const localStorageService = inject(LocalStorageService);
            const catalogUiOptions = inject(NCatalogUi.OPTIONS);
            const advancedCommerceStoreService = inject(AdvancedCommerceStoreService);
            const activatedRoute = inject(ActivatedRoute);

            return {
                onInit() {
                    /**
                     * Initialize listing size instance on init
                     */
                    patchState(store, {
                        listingSize: new ListingSize(client, localStorageService, catalogUiOptions.listing.category.size || [])
                    });

                    /**
                     * Update active sorting on request load
                     */
                    effect(() => {
                        const response = store.response();

                        if (response) {
                            const sorting = advancedCommerceStoreService.getSorting(response);
                            const currentSorting = store.queryParamSort() || 'default';

                            untracked(() => {
                                store.sortingAllSet(
                                    sorting.map(item => {
                                        return {
                                            ...item,
                                            active: item.code === currentSorting
                                        };
                                    })
                                );

                                // update sorting state if query param is different
                                patchState(store, { sorting: currentSorting });
                            });
                        }
                    });

                    /**
                     * Update filters data on request load
                     */
                    effect(() => {
                        const response = store.response();
                        const element = store.element();

                        if (response && element) {
                            const filters = advancedCommerceStoreService.getFilters('products', response, element);
                            const filtersSelection = advancedCommerceStoreService.getFiltersSelection('products', response, element);

                            untracked(() => {
                                const data = store.data();

                                if (filters !== undefined && !data.is_paging_request) {
                                    store.filtersAllSet(filters);
                                }

                                const updatePriceFromMeta = () => {
                                    const priceMeta = filters?.find(filter => filter.id === 'price')?.meta;

                                    if (priceMeta) {
                                        const filtersPriceFrom = priceMeta.from || 0;
                                        const filtersPriceTo = priceMeta.to || 0;

                                        patchState(store, { filtersPriceFrom });
                                        patchState(store, { filtersPriceTo });
                                    }
                                };

                                if (filtersSelection !== undefined && !data.is_paging_request) {
                                    const priceItems = filtersSelection.find(selection => selection.id === 'price')?.items;

                                    if (priceItems) {
                                        const priceMeta = filters?.find(filter => filter.id === 'price')?.meta;

                                        const filtersPriceFrom = priceItems[0].from || priceMeta?.from || 0;
                                        const filtersPriceTo = priceItems[0].to || priceMeta?.to || 0;

                                        patchState(store, { filtersPriceFrom });
                                        patchState(store, { filtersPriceTo });
                                    } else {
                                        updatePriceFromMeta();
                                    }

                                    store.filtersSetSelection(filtersSelection);
                                } else {
                                    updatePriceFromMeta();
                                }
                            });
                        }
                    });

                    /**
                     * Watch for update of filters
                     */
                    effect(() => {
                        const filtersSelection = store.filtersSelection();

                        untracked(() => {
                            const response = store.response();
                            const element = store.element();
                            const currentFiltersSelection =
                                advancedCommerceStoreService.getFiltersSelection('products', response, element) || [];

                            const noFiltersParams = !Object.keys(activatedRoute.snapshot.queryParams).find(
                                key => !['pg', 'sort', 'q'].includes(key)
                            );

                            const rangeFilter = Object.keys(activatedRoute.snapshot.queryParams).find(key => ['price'].includes(key));
                            let emptyPrice = false;

                            if (rangeFilter) {
                                const [from, to] = activatedRoute.snapshot.queryParams['price'].split('-');

                                if (!from && !to) {
                                    emptyPrice = true;
                                }
                            }

                            if (
                                !isEqual(currentFiltersSelection, filtersSelection) ||
                                (filtersSelection.length && noFiltersParams) ||
                                emptyPrice
                            ) {
                                advancedCommerceStoreService.setFiltersSelection(filtersSelection);
                            }
                        });
                    });

                    /**
                     * Watch for update sorting and update query param `sort`
                     */
                    effect(() => {
                        const sorting = store.sorting();

                        untracked(() => {
                            const currentSorting = store.queryParamSort() || 'default';

                            if (currentSorting !== sorting) {
                                advancedCommerceStoreService.setSorting(sorting || currentSorting);
                            }
                        });
                    });
                }
            };
        })
    );
}
